import type { ReactNode } from 'react'
import StripeMotif from './StripeMotif'

/* Shared dark-atmosphere hero wrapper. Solid black + warm pulsing
   glow top-right + cool/blue pulsing glow bottom-left + the WAVBEE
   stripe motif anchored to the bottom-right corner. Includes the
   navbar-clearance top padding (pt-32 / lg:pt-36) so children render
   below the fixed .marketing-nav-wrap.

   Used by: Stores, Apply, About, Blog, BlogArchive, Compare, Contact,
   Help, Playlists, VevoDistribution. */

interface AtmosphericHeroProps {
  /** Section min-height. Defaults to 56vh — text-only heros. Use a
   *  larger value (e.g. '62vh') when the hero carries a stat strip. */
  minHeight?: string
  /** Extra classes on the inner container (the one with the
   *  navbar-clearance padding + max-width container). */
  containerClassName?: string
  /** Hide the bottom-right stripe motif if a page wants pure
   *  glow-only atmosphere. */
  hideStripes?: boolean
  children: ReactNode
}

export default function AtmosphericHero({
  minHeight = '56vh',
  containerClassName = '',
  hideStripes = false,
  children
}: AtmosphericHeroProps) {
  return (
    <section className="marketing-hero-section hero-v2 relative overflow-hidden" style={{ minHeight }}>
      <div className="hero-spot-warm" aria-hidden="true" />
      <div className="hero-spot-cool" aria-hidden="true" />
      <div className="hero-bottom-fade" aria-hidden="true" />

      {!hideStripes && (
        <div className="pointer-events-none absolute -bottom-44 -right-44 hidden lg:block" style={{ zIndex: 1 }}>
          <StripeMotif size={360} opacity={0.28} />
        </div>
      )}

      {/* pt-32/lg:pt-40 clears the fixed 5rem nav. pb-16/lg:pb-20 is
          deliberately modest — the next section now owns its own
          py-* (top padding) so the hero just needs to close cleanly
          without doubling the gap. */}
      <div className={`container relative z-10 mx-auto px-6 pb-16 pt-32 lg:pb-20 lg:pt-40 ${containerClassName}`}>
        {children}
      </div>
    </section>
  )
}
